import { WebSocketConnectionMode } from "@/types/api/SimulatorType"

import {
  JSON_AUDIO_NON_AUDIO_EVENT_MESSAGE_TYPES,
  JSON_AUDIO_TEMPLATE,
  TWILIO_AUDIO_TEMPLATE,
  TWILIO_DTMF_TEMPLATE,
  TWILIO_INITIALIZATION_JSON,
  WEBSOCKET_COMPAT_PROFILE_GENERIC,
  WEBSOCKET_COMPAT_PROFILE_JSON_AUDIO,
  type WebSocketSimulatorConfigData,
} from "./types"

export const CUSTOM_WEBSOCKET_PRESET = "custom"
export const JSON_AUDIO_PRESET = "json_audio"
export const TWILIO_PRESET = "twilio"

export type WebSocketConfigPreset =
  | typeof CUSTOM_WEBSOCKET_PRESET
  | typeof JSON_AUDIO_PRESET
  | typeof TWILIO_PRESET

export const JSON_AUDIO_PRESET_METADATA: Partial<WebSocketSimulatorConfigData> =
  {
    connection_mode: WebSocketConnectionMode.DIRECT,
    initialization_json: "",
    send_sample_rate_hertz: 16000,
    receive_sample_rate_hertz: 16000,
    handshake_ready_message_type: "",
    handshake_requires_session_id: false,
    send_audio_template: JSON_AUDIO_TEMPLATE,
    message_type_path: "action",
    audio_message_type_value: "audio_message",
    audio_data_path: "audio_bytes",
    audio_encoding: "pcm",
    receive_audio_channels: 1,
    non_audio_event_message_types: [
      ...JSON_AUDIO_NON_AUDIO_EVENT_MESSAGE_TYPES,
    ],
    websocket_compat_profile: WEBSOCKET_COMPAT_PROFILE_JSON_AUDIO,
  }

export const TWILIO_PRESET_METADATA: Partial<WebSocketSimulatorConfigData> = {
  connection_mode: WebSocketConnectionMode.DIRECT,
  initialization_json: TWILIO_INITIALIZATION_JSON,
  send_sample_rate_hertz: 8000,
  receive_sample_rate_hertz: 8000,
  // Twilio streams never send a ready message, so waiting for one drops all inbound audio.
  handshake_ready_message_type: "",
  handshake_requires_session_id: false,
  send_audio_template: TWILIO_AUDIO_TEMPLATE,
  send_dtmf_template: TWILIO_DTMF_TEMPLATE,
  message_type_path: "event",
  audio_message_type_value: "media",
  audio_data_path: "media.payload",
  audio_encoding: "ulaw",
  receive_audio_channels: 1,
  non_audio_event_message_types: [],
  websocket_compat_profile: WEBSOCKET_COMPAT_PROFILE_GENERIC,
}

export const TWIML_WEBHOOK_MODE_METADATA: Partial<WebSocketSimulatorConfigData> =
  {
    ...TWILIO_PRESET_METADATA,
    connection_mode: WebSocketConnectionMode.TWIML_WEBHOOK,
    // The backend builds connected/start frames from the returned TwiML.
    initialization_json: undefined,
    voice_http_method: "POST",
    voice_http_headers: "{}",
    voice_form_fields: "{}",
  }

export function withTwimlWebhookRuntimeDefaults(
  metadata: WebSocketSimulatorConfigData,
): WebSocketSimulatorConfigData {
  return {
    ...metadata,
    send_sample_rate_hertz:
      metadata.send_sample_rate_hertz === undefined
        ? TWIML_WEBHOOK_MODE_METADATA.send_sample_rate_hertz
        : metadata.send_sample_rate_hertz,
    receive_sample_rate_hertz:
      metadata.receive_sample_rate_hertz === undefined
        ? TWIML_WEBHOOK_MODE_METADATA.receive_sample_rate_hertz
        : metadata.receive_sample_rate_hertz,
    audio_encoding:
      metadata.audio_encoding === undefined
        ? TWIML_WEBHOOK_MODE_METADATA.audio_encoding
        : metadata.audio_encoding,
  }
}

export const JSON_AUDIO_PRESET_RESET: Partial<WebSocketSimulatorConfigData> = {
  ...(Object.fromEntries(
    Object.keys(JSON_AUDIO_PRESET_METADATA).map((key) => [key, undefined]),
  ) as Partial<WebSocketSimulatorConfigData>),
  // Keep generic routing explicit so custom Checkmate-org agents do not fall
  // back to the legacy raw-PCM simulator after leaving the preset.
  websocket_compat_profile: WEBSOCKET_COMPAT_PROFILE_GENERIC,
}

// Clears every field either preset sets, so switching to Custom from one preset
// cannot leave the other preset's values behind.
export const WEBSOCKET_PRESET_RESET: Partial<WebSocketSimulatorConfigData> = {
  ...(Object.fromEntries(
    [
      ...Object.keys(JSON_AUDIO_PRESET_METADATA),
      ...Object.keys(TWILIO_PRESET_METADATA),
    ].map((key) => [key, undefined]),
  ) as Partial<WebSocketSimulatorConfigData>),
  websocket_compat_profile: WEBSOCKET_COMPAT_PROFILE_GENERIC,
}

// JSON template strings compare by parsed structure rather than raw text, so
// an agent saved under an older compact/pretty formatting of a preset's
// template still matches that preset instead of misdetecting as Custom.
function normalizeJsonStringForComparison(value: unknown): unknown {
  if (typeof value !== "string") {
    return value
  }
  try {
    return JSON.stringify(JSON.parse(value))
  } catch {
    return value
  }
}

function presetValuesMatch(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return (
      a.length === b.length && a.every((value, index) => value === b[index])
    )
  }
  return (
    normalizeJsonStringForComparison(a) === normalizeJsonStringForComparison(b)
  )
}

function metadataMatchesPreset(
  metadata: Partial<WebSocketSimulatorConfigData>,
  preset: Partial<WebSocketSimulatorConfigData>,
): boolean {
  return Object.entries(preset).every(([key, value]) => {
    return presetValuesMatch(
      metadata[key as keyof WebSocketSimulatorConfigData],
      value,
    )
  })
}

export function getActiveWebSocketPreset(
  metadata: Partial<WebSocketSimulatorConfigData>,
): WebSocketConfigPreset {
  if (metadataMatchesPreset(metadata, JSON_AUDIO_PRESET_METADATA)) {
    return JSON_AUDIO_PRESET
  }
  if (metadataMatchesPreset(metadata, TWILIO_PRESET_METADATA)) {
    return TWILIO_PRESET
  }
  return CUSTOM_WEBSOCKET_PRESET
}
